export const EmployeeDataSection = {
  Unknown: 'Unknown',
  WorkInfo: 'Work Info',
  Employment: 'Employment',
  ContractEmployment: 'Contract Employment',
  PersonalInfo: 'Personal Info',
  Salary: 'Salary',
  Bonus: 'Bonus',
  ExceptionalAward: 'Exceptional Award',
  MedicalInsurance: 'Medical Insurance',
  LifeInsurance: 'Life Insurance',
  BankAccount: 'Bank Account',
  PmStaticInfo: 'PM Static Info',
  PmInfo: 'PM Info',
  PmAnnualPerformance: 'PM Annual Performance',
  DeferredProgram: 'Deferred Program',
  CostDistribution: 'Cost Distribution',
  FinanceManagedData: 'Finance Managed Data',
  IdProof: 'ID Proof',
  Visa: 'Visa',
  DependentInfo: 'Dependent Info',
  EmergencyContact: 'Emergency Contact',
  EmploymentHistory: 'Employment History',
  GroupEmploymentHistory: 'Group Employment History',
  Education: 'Education',
  Qualification: 'Qualification',
  BackgroundCheck: 'Background Check',
  EmployeeDocument: 'Employee Document',
  AnnualPerformance: 'Annual Performance',
  WarningLetter: 'Warning Letter',
  PerformanceImprovementPlan: 'Performance Improvement Plan',
  // Module-record sections added by the entity-scoped access migration. Each of these names a
  // record that IS a section in its own right, rather than a field group on the employee.
  StaffCost: 'Staff Cost',
  TimeOff: 'Time Off',
  OnboardingRecord: 'Onboarding Record',
  Checklist: 'Checklist',
  Promotion: 'Promotion',
  Probation: 'Probation',
  Overtime: 'Overtime',
  Appraisal: 'Appraisal',
  RewardExercise: 'Reward Exercise',
}

export const GroupedEmployeeDataSection = {
  BasicInfo: [EmployeeDataSection.WorkInfo, EmployeeDataSection.Employment, EmployeeDataSection.ContractEmployment, EmployeeDataSection.PersonalInfo],
  PmInfo: [EmployeeDataSection.PmInfo, EmployeeDataSection.PmStaticInfo, EmployeeDataSection.PmAnnualPerformance],
  Compensation: [EmployeeDataSection.Salary, EmployeeDataSection.Bonus, EmployeeDataSection.ExceptionalAward],
}

// this should match Backend request field names
export const OnboardingSectionKeyMap = {
  [EmployeeDataSection.WorkInfo]: 'workInfo',
  [EmployeeDataSection.Employment]: 'employment',
  [EmployeeDataSection.PersonalInfo]: 'personalInfo',
  [EmployeeDataSection.Salary]: 'salary',
  [EmployeeDataSection.Bonus]: 'bonuses',
  [EmployeeDataSection.ContractEmployment]: 'contractEmployments',
  [EmployeeDataSection.BankAccount]: 'bankAccounts',
  [EmployeeDataSection.PmStaticInfo]: 'pmStaticInfo',
  [EmployeeDataSection.PmInfo]: 'pmInfo',
  [EmployeeDataSection.Education]: 'educations',
  [EmployeeDataSection.EmploymentHistory]: 'employmentHistories',
  [EmployeeDataSection.Qualification]: 'qualifications',
  [EmployeeDataSection.DependentInfo]: 'dependentInfos',
  [EmployeeDataSection.EmergencyContact]: 'emergencyContacts',
  [EmployeeDataSection.IdProof]: 'identificationDocuments',
  [EmployeeDataSection.Visa]: 'visas',
  [EmployeeDataSection.ContractEmployment]: 'contractEmployments',
}

export const ScdEmployeeDataSection = {
  WorkInfo: EmployeeDataSection.WorkInfo,
  PersonalInfo: EmployeeDataSection.PersonalInfo,
  Employment: EmployeeDataSection.Employment,
  PmStaticInfo: EmployeeDataSection.PmStaticInfo,
  PmInfo: EmployeeDataSection.PmInfo,
  Salary: EmployeeDataSection.Salary,
  DeferredProgram: EmployeeDataSection.DeferredProgram,
  CostDistribution: EmployeeDataSection.CostDistribution,
  FinanceManagedData: EmployeeDataSection.FinanceManagedData,
}

/**
 * Sections the backend classifies as SENSITIVE, mirroring
 * `EmployeeDataSection.GetSensitiveSectionNames()` in `HumanCapital.Portal.Common`.
 *
 * A sensitive section is one where the backend makes the department-routed check mandatory: the
 * employee's department decides whether their grant is read from the `general` set or the `hc` set,
 * so holding Salary in an entity's general set does NOT let you read a Human Capital employee's
 * salary at that same entity.
 *
 * ## This is a fallback, not the source of truth
 *
 * Prefer the `sensitiveSections` array returned by
 * `GET /PortalConfiguration/GetAllEmployeeDataAccessClaims`, which is generated from the backend
 * constant at request time and therefore cannot drift. Use this list only where no such response is
 * available (static labelling, forms, documentation).
 *
 * If you add or reclassify a section, change the backend constant first — a stale copy here means
 * the UI stops flagging data the backend still treats as sensitive, which is a silent failure.
 */
export const SensitiveEmployeeDataSections: readonly string[] = [
  EmployeeDataSection.Salary,
  EmployeeDataSection.Bonus,
  EmployeeDataSection.ExceptionalAward,
  EmployeeDataSection.Appraisal,
  EmployeeDataSection.AnnualPerformance,
  EmployeeDataSection.StaffCost,
  EmployeeDataSection.RewardExercise,
]

/**
 * True when `section` is sensitive per the fallback list above. Pass `sensitiveSections` from an API
 * response as the second argument to classify against the backend's live list instead.
 */
export const isSensitiveEmployeeDataSection = (
  section: string,
  sensitiveSections: readonly string[] = SensitiveEmployeeDataSections,
): boolean => sensitiveSections.includes(section)